import React, { useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Tabs, Tab } from "@mui/material";
import HeaderTitleContent from "../../../../common/components/atom/HeaderTitleContent/HeaderTitleContent";
import { useExtractGroups } from "../../hooks/useExtract";
import {
  useActiveExtractJob,
  useCreateExtractJob,
} from "../../hooks/useExtractJobs";
import { useKnownMeterTags } from "../../../meterTags/hooks/useMeterTags";
import { ExtractJobConflictError } from "../../services/extractAPI";
import {
  ExtractAggregation,
  ExtractConfig,
  ExtractGroup,
} from "../../types/extract.types";
import DeviceTypeSectionView from "./DeviceTypeSection";
import ExtractionOptionsSectionView from "./ExtractionOptionsSection";
import ConfigurationSectionView from "./ConfigurationSection";
import ExtractJobsListView from "./ExtractJobsListView";
import styles from "./ExtractPage.module.scss";

const MAX_RANGE_DAYS = 31;

type FormTab = "new" | "list";

export default function ExtractPage() {
  const [tab, setTab] = useState<FormTab>("new");

  // The jobs query is owned by whichever subtree needs it: useActiveExtractJob
  // inside NewExtractTab (form tab) and useExtractJobsList inside
  // ExtractJobsListView (list tab). Both share the same react-query cache key
  // and useCreateExtractJob invalidates that cache on submit, so a fresh job
  // appears on tab switch without an explicit refetch here.

  return (
    <div className={styles["extract-page"]}>
      <HeaderTitleContent>Extract</HeaderTitleContent>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as FormTab)}
        sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 2 }}
      >
        <Tab value="new" label="Nouvelle extraction" />
        <Tab value="list" label="Mes extractions" />
      </Tabs>

      {tab === "new" ? (
        <NewExtractTab onSubmitted={() => setTab("list")} />
      ) : (
        <ExtractJobsListView />
      )}
    </div>
  );
}

interface NewExtractTabProps {
  onSubmitted: () => void;
}

function NewExtractTab({ onSubmitted }: NewExtractTabProps) {
  const { data: groups, isLoading: groupsLoading } = useExtractGroups();
  const createJob = useCreateExtractJob();
  const activeJob = useActiveExtractJob();
  const knownTags = useKnownMeterTags();

  const [selectedGroup, setSelectedGroup] = useState<ExtractGroup | null>(null);
  const [selectedExtract, setSelectedExtract] = useState<ExtractConfig | null>(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(1, "day"));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [aggregation, setAggregation] = useState<ExtractAggregation>("hourly");
  const [tags, setTags] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<{
    kind: "success" | "error" | "info";
    text: string;
  } | null>(null);

  const maxDate = dayjs();
  const minEndDate = startDate || dayjs().subtract(MAX_RANGE_DAYS, "day");
  const maxEndDate = useMemo(() => {
    if (!startDate) return maxDate;
    const cap = startDate.add(MAX_RANGE_DAYS, "day");
    return cap.isBefore(maxDate) ? cap : maxDate;
  }, [startDate, maxDate]);

  const isDateValid = useMemo(() => {
    if (!startDate || !endDate) return false;
    if (endDate.isBefore(startDate)) return false;
    if (endDate.diff(startDate, "day") > MAX_RANGE_DAYS) return false;
    return true;
  }, [startDate, endDate]);

  const hasActiveJob = !!activeJob;
  const canStart = !!(
    selectedGroup &&
    selectedExtract &&
    isDateValid &&
    !hasActiveJob &&
    !createJob.isLoading
  );

  const handleGroupSelect = (group: ExtractGroup) => {
    setSelectedGroup(group);
    setSelectedExtract(null);
    setFeedback(null);
  };

  const handleExtractSelect = (extract: ExtractConfig) => {
    setSelectedExtract(extract);
    setFeedback(null);
  };

  const handleStartDateChange = (val: Dayjs | null) => {
    setStartDate(val);
    if (val && endDate && endDate.diff(val, "day") > MAX_RANGE_DAYS) {
      setEndDate(val.add(MAX_RANGE_DAYS, "day"));
    }
  };

  const handleStart = () => {
    if (!selectedGroup || !selectedExtract || !startDate || !endDate) return;

    setFeedback(null);
    createJob.mutate(
      {
        groupId: selectedGroup.id,
        extractId: selectedExtract.id,
        startDate: startDate.startOf("day").toISOString(),
        endDate: endDate.endOf("day").toISOString(),
        aggregation,
        tags,
      },
      {
        onSuccess: () => {
          setFeedback({
            kind: "success",
            text:
              "Extraction lancée. Vous recevrez un email à la fin et pourrez la retrouver dans « Mes extractions ».",
          });
          onSubmitted();
        },
        onError: (err) => {
          if (err instanceof ExtractJobConflictError) {
            setFeedback({
              kind: "info",
              text:
                "Une extraction est déjà en cours pour ce compte. Consultez l'onglet « Mes extractions ».",
            });
            onSubmitted();
            return;
          }
          setFeedback({
            kind: "error",
            text: err.message || "Échec du lancement de l'extraction.",
          });
        },
      }
    );
  };

  return (
    <div className={styles["extract-page__layout"]}>
      <div className={styles["extract-page__left"]}>
        <div className={styles["extract-page__card"]}>
          <DeviceTypeSectionView
            groups={groups}
            isLoading={groupsLoading}
            selectedGroupId={selectedGroup?.id ?? null}
            onGroupSelect={handleGroupSelect}
          />
        </div>

        <div className={styles["extract-page__card"]}>
          <ExtractionOptionsSectionView
            selectedGroup={selectedGroup}
            selectedExtract={selectedExtract}
            onExtractSelect={handleExtractSelect}
          />
        </div>
      </div>

      <div className={`${styles["extract-page__card"]} ${styles["extract-page__right"]}`}>
        <ConfigurationSectionView
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={setEndDate}
          maxDate={maxDate}
          minEndDate={minEndDate}
          maxEndDate={maxEndDate}
          aggregation={aggregation}
          onAggregationChange={setAggregation}
          tags={tags}
          onTagsChange={setTags}
          tagSuggestions={knownTags}
          canStart={canStart}
          isSubmitting={createJob.isLoading}
          hasActiveJob={hasActiveJob}
          onStart={handleStart}
        />
      </div>

      {feedback && (
        <div
          className={[
            styles["extract-page__activity"],
            styles["extract-page__feedback"],
            styles["extract-page__feedback--" + feedback.kind],
          ].join(" ")}
          role={feedback.kind === "error" ? "alert" : "status"}
        >
          {feedback.text}
        </div>
      )}
    </div>
  );
}
